import { STATS_CENTER_URL } from '../../lib/config';
import { ROUTES } from '../../lib/routes';
import { navigate } from '../../router';
import { el } from '../../utils/dom';
import {
  clearPortalSession,
  getPortalAccount,
  isPortalAdmin,
  isPortalLoggedIn,
} from '../../lib/portalSession';
import { fetchPortalMe } from '../../api/portalMe';

export type SiteHeaderApi = {
  element: HTMLElement;
  drawerElement: HTMLElement;
  setActivePath: (path: string) => void;
  openMenu: () => void;
  closeMenu: () => void;
};

function externalNavLink(href: string, label: string): HTMLAnchorElement {
  const a = el('a', {
    className: 'ev123-nav__link ev123-nav__link--sub',
    href,
    target: '_blank',
    rel: 'noopener noreferrer',
  }) as HTMLAnchorElement;
  a.textContent = label;
  return a;
}

function navLink(href: string, label: string, sub: boolean): HTMLAnchorElement {
  const a = el('a', {
    className: `ev123-nav__link${sub ? ' ev123-nav__link--sub' : ''}`,
    href,
    'data-nav': '',
  }) as HTMLAnchorElement;
  a.textContent = label;
  return a;
}

export function createSiteHeader(): SiteHeaderApi {
  const drawer = el('div', { className: 'ev123-drawer', 'data-open': 'false' });
  const backdrop = el('div', { className: 'ev123-drawer__backdrop' });
  const panel = el('div', { className: 'ev123-drawer__panel' });

  const drawerNav = el('nav', { className: 'ev123-drawer__nav', 'aria-label': '모바일 메뉴' });
  const mobileLinkEls: HTMLAnchorElement[] = [];

  const staticMobile: [string, string][] = [
    [ROUTES.newInstall, '충전기 설치'],
    [ROUTES.consult, '설치 상담'],
    [ROUTES.installCaseBoard, '설치사례'],
    [ROUTES.resources, '자료실'],
    [ROUTES.differentiatedServices, '차별화서비스'],
    [ROUTES.serviceApply, '서비스 신청'],
    [STATS_CENTER_URL, '전기차 충전시장 통계'],
  ];
  for (const [href, label] of staticMobile) {
    const isExternal = href.startsWith('http');
    const a = el('a', {
      className: 'ev123-drawer__link',
      href,
      ...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : { 'data-nav': '' }),
    }) as HTMLAnchorElement;
    a.textContent = label;
    mobileLinkEls.push(a);
    drawerNav.appendChild(a);
  }

  const drawerAuth = el('div', { className: 'ev123-drawer__auth' });

  const closeBtn = el('button', {
    type: 'button',
    className: 'ev123-menu-btn',
    'aria-label': '메뉴 닫기',
  });
  closeBtn.innerHTML =
    '<span aria-hidden="true" style="font-size:1.25rem;line-height:1">&times;</span>';

  const drawerHead = el('div', { className: 'ev123-drawer__head' }, [
    el('span', { className: 'ev123-footer__brand', text: 'EV123' }),
    closeBtn,
  ]);

  panel.append(drawerHead, drawerNav, drawerAuth);
  drawer.append(backdrop, panel);

  const header = el('header', { className: 'ev123-header' });
  const inner = el('div', { className: 'ev123-container ev123-header__inner' });

  const logo = el('a', {
    className: 'ev123-logo ev123-logo--responsive ev123-logo--ci',
    href: ROUTES.homePublic,
    'data-nav': '',
    'aria-label': 'EV123 홈으로 이동',
  }) as HTMLAnchorElement;
  logo.appendChild(
    el('img', {
      className: 'ev123-logo__img',
      src: '/ev123-logo-home.png',
      alt: 'EV123 전기차충전기 신청 포털서비스',
      width: '200',
      height: '48',
      decoding: 'async',
    })
  );

  const nav = el('nav', { className: 'ev123-nav', 'aria-label': '주요 메뉴' });
  const navNew = navLink(ROUTES.newInstall, '충전기 설치', false);
  const navConsult = navLink(ROUTES.consult, '설치 상담', false);
  const navCases = navLink(ROUTES.installCaseBoard, '설치사례', false);
  const navResources = navLink(ROUTES.resources, '자료실', false);
  const navDiff = navLink(ROUTES.differentiatedServices, '차별화서비스', false);
  const navServiceApply = navLink(ROUTES.serviceApply, '서비스 신청', false);
  const navStats = externalNavLink(STATS_CENTER_URL, '전기차 충전시장 통계');

  const navGuest = el('span', { className: 'ev123-nav__auth-cluster' });
  const navLogin = navLink(ROUTES.login, '로그인', true);
  const navSignup = navLink(ROUTES.signup, '회원가입', true);
  navGuest.append(navLogin, navSignup);

  const navUser = el('span', { className: 'ev123-nav__auth-cluster', style: 'display:none' });
  const navMy = navLink(ROUTES.myApplications, '신청내역', true);
  const navProfile = navLink(ROUTES.myProfile, '프로필', true);
  navUser.append(navMy, navProfile);

  const navAdminBtn = el('a', {
    className: 'ev123-btn ev123-btn--header-subtle ev123-btn--sm ev123-nav__admin-btn',
    href: ROUTES.manage,
    'data-nav': '',
    role: 'button',
    'aria-label': '관리자 모드 진입',
    style: 'display:none',
  }) as HTMLAnchorElement;
  navAdminBtn.textContent = '관리자 모드';

  nav.append(
    navNew,
    navConsult,
    navCases,
    navResources,
    navDiff,
    navServiceApply,
    navStats,
    navGuest,
    navUser,
    navAdminBtn,
  );

  const actions = el('div', { className: 'ev123-header__actions' });

  const logoutBtn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--header-subtle ev123-btn--sm',
    style: 'display:none',
  }) as HTMLButtonElement;
  logoutBtn.textContent = '로그아웃';

  const menuBtn = el('button', {
    type: 'button',
    className: 'ev123-menu-btn',
    'aria-label': '메뉴 열기',
    'aria-expanded': 'false',
  });
  menuBtn.innerHTML =
    '<span aria-hidden="true" style="display:flex;flex-direction:column;gap:5px;width:18px">' +
    '<span style="height:2px;background:#0c352f;border-radius:1px"></span>' +
    '<span style="height:2px;background:#0c352f;border-radius:1px"></span>' +
    '<span style="height:2px;background:#0c352f;border-radius:1px"></span>' +
    '</span>';

  actions.append(logoutBtn, menuBtn);
  inner.append(logo, nav, actions);
  header.appendChild(inner);

  function openMenu(): void {
    drawer.setAttribute('data-open', 'true');
    menuBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function closeMenu(): void {
    drawer.setAttribute('data-open', 'false');
    menuBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  closeBtn.addEventListener('click', () => closeMenu());
  backdrop.addEventListener('click', () => closeMenu());

  menuBtn.addEventListener('click', () => {
    if (drawer.getAttribute('data-open') === 'true') closeMenu();
    else openMenu();
  });

  logoutBtn.addEventListener('click', () => {
    clearPortalSession();
    navigate(ROUTES.homePublic);
    closeMenu();
  });

  const navMap: [HTMLAnchorElement, string][] = [
    [navNew, ROUTES.newInstall],
    [navConsult, ROUTES.consult],
    [navCases, ROUTES.installCaseBoard],
    [navResources, ROUTES.resources],
    [navDiff, ROUTES.differentiatedServices],
    [navServiceApply, ROUTES.serviceApply],
    [navLogin, ROUTES.login],
    [navSignup, ROUTES.signup],
    [navMy, ROUTES.myApplications],
    [navProfile, ROUTES.myProfile],
    [navAdminBtn, ROUTES.manage],
  ];

  function fillDrawerAuth(): void {
    drawerAuth.replaceChildren();
    const acc = getPortalAccount();
    if (isPortalLoggedIn()) {
      if (acc) {
        drawerAuth.appendChild(
          el('p', {
            className: 'ev123-drawer__account',
            text: `${acc} 님`,
          })
        );
      }
      const a1 = el('a', {
        className: 'ev123-drawer__link',
        href: ROUTES.myApplications,
        'data-nav': '',
      }) as HTMLAnchorElement;
      a1.textContent = '신청내역';
      const a2 = el('a', {
        className: 'ev123-drawer__link',
        href: ROUTES.myProfile,
        'data-nav': '',
      }) as HTMLAnchorElement;
      a2.textContent = '프로필';
      drawerAuth.append(a1, a2);
      if (isPortalAdmin()) {
        const aMng = el('a', {
          className: 'ev123-btn ev123-btn--header-subtle ev123-btn--block',
          href: ROUTES.manage,
          'data-nav': '',
          role: 'button',
        }) as HTMLAnchorElement;
        aMng.textContent = '관리자 모드';
        drawerAuth.appendChild(aMng);
      }
      const lo = el('button', {
        type: 'button',
        className: 'ev123-btn ev123-btn--header-subtle ev123-btn--block',
      }) as HTMLButtonElement;
      lo.textContent = '로그아웃';
      lo.addEventListener('click', () => {
        clearPortalSession();
        navigate(ROUTES.homePublic);
        closeMenu();
      });
      drawerAuth.appendChild(lo);
    } else {
      const aLogin = el('a', {
        className: 'ev123-drawer__link',
        href: ROUTES.login,
        'data-nav': '',
      }) as HTMLAnchorElement;
      aLogin.textContent = '로그인';
      const aSignup = el('a', {
        className: 'ev123-btn ev123-btn--header-subtle ev123-btn--block',
        href: ROUTES.signup,
        'data-nav': '',
      }) as HTMLAnchorElement;
      aSignup.textContent = '회원가입';
      drawerAuth.append(aLogin, aSignup);
    }
  }

  function syncAuth(): void {
    const in_ = isPortalLoggedIn();
    navGuest.style.display = in_ ? 'none' : '';
    navUser.style.display = in_ ? '' : 'none';
    logoutBtn.style.display = in_ ? '' : 'none';
    navAdminBtn.style.display = in_ && isPortalAdmin() ? '' : 'none';
    fillDrawerAuth();
  }

  window.addEventListener('ev123-auth-changed', syncAuth);
  syncAuth();

  if (isPortalLoggedIn()) {
    void fetchPortalMe();
  }

  function setActivePath(path: string): void {
    for (const [elLink, p] of navMap) {
      const on = p === path;
      elLink.classList.toggle('ev123-nav__link--active', on);
    }
    for (const a of mobileLinkEls) {
      const href = a.getAttribute('href');
      if (!href) continue;
      const on = href === path;
      a.classList.toggle('ev123-drawer__link--active', on);
    }
  }

  return {
    element: header,
    drawerElement: drawer,
    setActivePath,
    openMenu,
    closeMenu,
  };
}
